import React, { useState } from 'react';
import { Button } from '../components/Button';
import { Check } from 'lucide-react';
import { Clock } from 'lucide-react';
import { DollarSign } from 'lucide-react';
import { Input } from '../components/Input';
import { Link } from '../components/Link';
import { Percent } from 'lucide-react';
import { Repeat } from 'lucide-react';
import { Select } from '../components/Select';
import { ShieldCheck } from 'lucide-react';
import { TrendingUp } from 'lucide-react';
import { UserCheck } from 'lucide-react';
import NavBar from "../components/navigation";
import Footer from "../components/Footer";

const INITIAL_CALCULATOR_VALUES = {
  vehiclePrice: '85000',
  downPayment: '15000',
  apr: '3.9',
  loanTerm: '60',
};

const INITIAL_CALCULATION = {
  monthlyPayment: 1289.19,
  loanAmount: 70000,
  totalInterest: 7351.4,
  totalAmount: 77351.4,
  term: 60,
};

function formatCurrency(value) {
  return `Ksh. ${Math.round(value).toLocaleString('en-KE')}`;
}

function calculateLoan(values) {
  const vehiclePrice = Number(values.vehiclePrice);
  const downPayment = Number(values.downPayment);
  const annualRate = Number(values.apr);
  const term = Number(values.loanTerm);

  if (!Number.isFinite(vehiclePrice) || vehiclePrice <= 0) {
    return { error: 'Enter a vehicle price greater than zero.' };
  }
  if (!Number.isFinite(downPayment) || downPayment < 0) {
    return { error: 'Enter a valid down payment.' };
  }
  if (downPayment >= vehiclePrice) {
    return { error: 'Down payment must be less than the vehicle price.' };
  }
  if (!Number.isFinite(annualRate) || annualRate < 0) {
    return { error: 'Enter a valid interest rate.' };
  }

  const loanAmount = vehiclePrice - downPayment;
  const monthlyRate = annualRate / 100 / 12;
  const monthlyPayment = monthlyRate === 0
    ? loanAmount / term
    : loanAmount * (monthlyRate * (1 + monthlyRate) ** term) / ((1 + monthlyRate) ** term - 1);
  const totalAmount = monthlyPayment * term;

  return {
    monthlyPayment,
    loanAmount,
    totalInterest: totalAmount - loanAmount,
    totalAmount,
    term,
  };
}

export const FinancingPage = ({
  className,
  children,
  variant,
  contentKey,
  ...props
}) => {
  const [calculatorValues, setCalculatorValues] = useState(INITIAL_CALCULATOR_VALUES);
  const [calculation, setCalculation] = useState(INITIAL_CALCULATION);
  const [calculatorError, setCalculatorError] = useState('');

  function updateCalculatorValue(event) {
    const { id, value } = event.target;
    setCalculatorValues((current) => ({ ...current, [id]: value }));
  }

  function handleCalculate(event) {
    event.preventDefault();
    const result = calculateLoan(calculatorValues);
    if (result.error) {
      setCalculatorError(result.error);
      return;
    }
    setCalculatorError('');
    setCalculation(result);
  }

  return (
    <>
      <NavBar />
      <div className="bg-white text-text">

      {/* =====================================================
          PAYMENT CALCULATOR
      ===================================================== */}
      <section
        id="payment_calculator"
        className="py-20 lg:py-28 bg-background"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-8">

          {/* Heading */}
          <div className="max-w-2xl mb-12">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-3">
              Financing
            </p>

            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark leading-tight mb-4">
              Know what you'll pay.
            </h2>

            <p className="text-lg text-muted leading-relaxed">
              Adjust the numbers below to get an estimate of your monthly
              vehicle payment.
            </p>
          </div>

          {/* Calculator */}
          <form onSubmit={handleCalculate} className="grid lg:grid-cols-[1.1fr_0.9fr] bg-white border border-border rounded-2xl overflow-hidden shadow-sm">

            {/* Form */}
            <div className="p-7 md:p-10 lg:p-12">

              <div className="grid sm:grid-cols-2 gap-6">

                {/* Vehicle Price */}
                <div className="sm:col-span-2">
                  <label
                    htmlFor="vehiclePrice"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Vehicle Price
                  </label>

                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted">
                      Ksh.
                    </span>

                    <Input
                      type="number"
                      id="vehiclePrice"
                      value={calculatorValues.vehiclePrice}
                      onChange={updateCalculatorValue}
                      min="1"
                      className="w-full pl-14 pr-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Down Payment */}
                <div>
                  <label
                    htmlFor="downPayment"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Down Payment
                  </label>

                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted">
                      Ksh.
                    </span>

                    <Input
                      type="number"
                      id="downPayment"
                      value={calculatorValues.downPayment}
                      onChange={updateCalculatorValue}
                      min="0"
                      className="w-full pl-14 pr-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* APR */}
                <div>
                  <label
                    htmlFor="apr"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Interest Rate
                  </label>

                  <div className="relative">
                    <Input
                      type="number"
                      id="apr"
                      value={calculatorValues.apr}
                      onChange={updateCalculatorValue}
                      min="0"
                      step="0.1"
                      className="w-full px-4 pr-12 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                    />

                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted">
                      %
                    </span>
                  </div>
                </div>

                {/* Loan Term */}
                <div className="sm:col-span-2">
                  <label
                    htmlFor="loanTerm"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Loan Term
                  </label>

                  <Select
                    id="loanTerm"
                    value={calculatorValues.loanTerm}
                    onChange={updateCalculatorValue}
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all cursor-pointer"
                  >
                    <option value="36">36 months (3 years)</option>
                    <option value="48">48 months (4 years)</option>
                    <option value="60">
                      60 months (5 years)
                    </option>
                    <option value="72">72 months (6 years)</option>
                  </Select>
                </div>

              </div>

              <Button
                variant="primary"
                contentKey="cta_30"
                id="calculateBtn"
                className="w-full mt-8 px-8 py-3.5  text-white font-bold rounded-lg transition-all cursor-pointer themed"
              >
                Calculate Payment
              </Button>

              {calculatorError && (
                <p className="mt-3 text-sm text-red-600" role="alert">
                  {calculatorError}
                </p>
              )}

            </div>

            {/* Result */}
            <div className="bg-dark text-white p-8 md:p-10 lg:p-12 flex flex-col justify-between">

              <div>

                <p className="text-sm font-semibold uppercase tracking-wider text-black mb-4">
                  Estimated monthly payment
                </p>

                <div
                  id="monthlyPayment"
                  className="text-5xl md:text-6xl font-heading font-bold tracking-tight text-black"
                >
                  {formatCurrency(calculation.monthlyPayment)}
                </div>

                <p className="mt-2 text-white/60">
                  for {calculation.term} months
                </p>

              </div>

              <div className="mt-10">

                <div className="space-y-4">

                  <div className="flex justify-between gap-4 text-sm">
                    <span className="text-white/60">
                      Total loan amount
                    </span>

                    <span
                      id="totalLoan"
                      className="font-semibold"
                    >
                      {formatCurrency(calculation.loanAmount)}
                    </span>
                  </div>

                  <div className="flex justify-between gap-4 text-sm">
                    <span className="text-white/60">
                      Total interest
                    </span>

                    <span
                      id="totalInterest"
                      className="font-semibold"
                    >
                      {formatCurrency(calculation.totalInterest)}
                    </span>
                  </div>

                  <div className="border-t border-white/10 pt-4 flex justify-between gap-4">
                    <span className="font-semibold">
                      Total amount
                    </span>

                    <span
                      id="totalAmount"
                      className="font-bold"
                    >
                      {formatCurrency(calculation.totalAmount)}
                    </span>
                  </div>

                </div>

                <p className="mt-8 text-xs leading-relaxed text-white/50">
                  Estimates are for informational purposes only. Actual
                  rates and terms may vary based on credit approval.
                </p>

              </div>

            </div>

          </form>
        </div>
      </section>


      {/* =====================================================
          FINANCING OPTIONS
      ===================================================== */}
      <section
        id="choose_your_path"
        className="py-20 lg:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <div className="max-w-2xl mb-12">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-3">
              Your options
            </p>

            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark leading-tight mb-4">
              Choose the way that works for you.
            </h2>

            <p className="text-lg text-muted leading-relaxed">
              Whether you want ownership, flexibility, or simplicity,
              there's an option for you.
            </p>
          </div>


          <div className="grid md:grid-cols-3 gap-6">

            {/* Traditional Loan */}
            <div className="border border-border rounded-2xl p-7 md:p-8 hover:border-primary/50 hover:shadow-md transition-all">

              <div className="flex items-center gap-4 mb-7">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>

                <div>
                  <h3 className="text-xl font-heading font-bold text-dark">
                    Traditional Loan
                  </h3>

                  <p className="text-sm text-muted">
                    Own your vehicle
                  </p>
                </div>
              </div>

              <div className="mb-7">
                <span className="text-4xl font-heading font-bold text-dark">
                  3.9%
                </span>

                <span className="text-muted ml-2">
                  APR starting at
                </span>
              </div>

              <ul className="space-y-4 mb-8">

                {[
                  'Flexible terms: 36–72 months',
                  'Own your vehicle outright',
                  'No mileage restrictions',
                  'Build equity over time',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-muted"
                  >
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}

              </ul>

              <Link
                contentKey="cta_31"
                href="/contact"
                className="block w-full text-center px-6 py-3 border border-primary text-primary font-bold rounded-lg hover:bg-primary hover:text-white transition-all"
              >
                Apply Now
              </Link>

            </div>


            {/* Lease */}
            <div className="border-2 border-primary rounded-2xl p-7 md:p-8 relative bg-primary/[0.03]">

              <div className="absolute top-5 right-5 bg-primary text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                Popular
              </div>

              <div className="flex items-center gap-4 mb-7 pr-16">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Repeat className="w-6 h-6 text-primary" />
                </div>

                <div>
                  <h3 className="text-xl font-heading font-bold text-dark">
                    Lease
                  </h3>

                  <p className="text-sm text-muted">
                    Maximum flexibility
                  </p>
                </div>
              </div>

              <div className="mb-7">
                <span className="text-4xl font-heading font-bold text-dark">
                  Ksh. 100,000
                </span>

                <span className="text-muted ml-2">
                  / month
                </span>
              </div>

              <ul className="space-y-4 mb-8">

                {[
                  'Lower monthly payments',
                  'Drive newer models',
                  'Warranty coverage included',
                  'Tax advantages for business use',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-muted"
                  >
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}

              </ul>

              <Link
                contentKey="cta_32"
                href="/contact"
                className="block w-full text-center px-6 py-3 bg-primary text-white font-bold rounded-lg themed transition-all"
              >
                Get Started
              </Link>

            </div>


            {/* Cash */}
            <div className="border border-border rounded-2xl p-7 md:p-8 hover:border-primary/50 hover:shadow-md transition-all">

              <div className="flex items-center gap-4 mb-7">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>

                <div>
                  <h3 className="text-xl font-heading font-bold text-dark">
                    Cash Purchase
                  </h3>

                  <p className="text-sm text-muted">
                    Simple ownership
                  </p>
                </div>
              </div>

              <div className="mb-7">
                <span className="text-4xl font-heading font-bold text-dark">
                  0%
                </span>

                <span className="text-muted ml-2">
                  financing fees
                </span>
              </div>

              <ul className="space-y-4 mb-8">

                {[
                  'No interest or finance charges',
                  'Potential for better negotiation',
                  'Immediate ownership',
                  'Simplified transaction',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-muted"
                  >
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}

              </ul>

              <Link
                contentKey="cta_33"
                href="/contact"
                className="block w-full text-center px-6 py-3 border border-primary text-primary font-bold rounded-lg hover:bg-primary hover:text-white transition-all"
              >
                Contact Us
              </Link>

            </div>

          </div>
        </div>
      </section>


      {/* =====================================================
          WHY MY CAR
      ===================================================== */}
      <section
        id="why_finance_with_apex_motors"
        className="py-20 lg:py-24 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <div className="max-w-2xl mb-12">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-3">
              Why My Car
            </p>

            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark leading-tight mb-4">
              Financing without the headache.
            </h2>

            <p className="text-lg text-muted leading-relaxed">
              We keep the process straightforward from application
              to approval.
            </p>
          </div>


          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">

            <div className="bg-white border border-border rounded-2xl p-7">
              <ShieldCheck className="w-8 h-8 text-primary mb-6" />

              <h3 className="text-lg font-heading font-bold text-dark mb-2">
                All Credit Welcome
              </h3>

              <p className="text-sm text-muted leading-relaxed">
                We work with multiple lenders to find solutions
                for different credit situations.
              </p>
            </div>


            <div className="bg-white border border-border rounded-2xl p-7">
              <Clock className="w-8 h-8 text-primary mb-6" />

              <h3 className="text-lg font-heading font-bold text-dark mb-2">
                Quick Approval
              </h3>

              <p className="text-sm text-muted leading-relaxed">
                Get pre-approved quickly with our streamlined
                application process.
              </p>
            </div>


            <div className="bg-white border border-border rounded-2xl p-7">
              <Percent className="w-8 h-8 text-primary mb-6" />

              <h3 className="text-lg font-heading font-bold text-dark mb-2">
                Competitive Rates
              </h3>

              <p className="text-sm text-muted leading-relaxed">
                Access competitive rates through our network
                of financing partners.
              </p>
            </div>


            <div className="bg-white border border-border rounded-2xl p-7">
              <UserCheck className="w-8 h-8 text-primary mb-6" />

              <h3 className="text-lg font-heading font-bold text-dark mb-2">
                Expert Guidance
              </h3>

              <p className="text-sm text-muted leading-relaxed">
                Our team helps you understand your options
                every step of the way.
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* =====================================================
          PRE-APPROVAL
      ===================================================== */}
      <section
        id="get_pre_approved"
        className="py-20 lg:py-28 bg-white"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-8">

          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20 items-start">

            {/* Intro */}
            <div className="lg:sticky lg:top-24">

              <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-3">
                Get started
              </p>

              <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark leading-tight mb-5">
                Get pre-approved.
              </h2>

              <p className="text-lg text-muted leading-relaxed mb-8">
                Tell us a little about yourself and we'll help you
                understand what financing options may be available.
              </p>

              <div className="space-y-4 text-sm text-muted">

                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>Simple application</span>
                </div>

                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>Fast response</span>
                </div>

                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>Your information stays secure</span>
                </div>

              </div>

            </div>


            {/* Form */}
            <form className="border border-border rounded-2xl p-7 md:p-10 space-y-6">

              <div className="grid md:grid-cols-2 gap-6">

                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Full Name *
                  </label>

                  <Input
                    type="text"
                    id="fullName"
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Email Address *
                  </label>

                  <Input
                    type="email"
                    id="email"
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                  />
                </div>

              </div>


              <div className="grid md:grid-cols-2 gap-6">

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Phone Number *
                  </label>

                  <Input
                    type="tel"
                    id="phone"
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                  />
                </div>

                <div>
                  <label
                    htmlFor="annualIncome"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Annual Income *
                  </label>

                  <Input
                    type="number"
                    id="annualIncome"
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                  />
                </div>

              </div>


              <div className="grid md:grid-cols-2 gap-6">

                <div>
                  <label
                    htmlFor="employmentStatus"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Employment Status *
                  </label>

                  <Select
                    id="employmentStatus"
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all cursor-pointer"
                  >
                    <option>Select status</option>
                    <option>Employed Full-Time</option>
                    <option>Employed Part-Time</option>
                    <option>Self-Employed</option>
                    <option>Retired</option>
                    <option>Other</option>
                  </Select>
                </div>

                <div>
                  <label
                    htmlFor="creditScore"
                    className="block text-sm font-semibold text-text mb-2"
                  >
                    Estimated Credit Score
                  </label>

                  <Select
                    id="creditScore"
                    className="w-full px-4 py-3.5 bg-white border border-border rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all cursor-pointer"
                  >
                    <option>Select range</option>
                    <option>Excellent (750+)</option>
                    <option>Good (700–749)</option>
                    <option>Fair (650–699)</option>
                    <option>Poor (Below 650)</option>
                    <option>Not Sure</option>
                  </Select>
                </div>

              </div>


              <div className="pt-2">

                <Button
                  contentKey="cta_34"
                  type="submit"
                  className="themed w-full px-8 py-3.5 bg-cta hover:bg-orange-600 text-white font-bold rounded-lg transition-all cursor-pointer"
                >
                  Submit Pre-Approval Application
                </Button>

                <p className="text-xs text-muted text-center leading-relaxed mt-4">
                  By submitting this form, you consent to My Car contacting
                  you regarding your financing inquiry. Your information is
                  secure and will not be shared with third parties.
                </p>

              </div>

            </form>

          </div>
        </div>
      </section>

    </div>
    <Footer />
    </>
  );
};
